import { IconAlertTriangle } from '@tabler/icons-react'
import { Component, type ErrorInfo, type ReactNode } from 'react'

import { IconTile } from '@/components/icon-tile'
import { Button } from '@/components/ui/button'
import { HttpError } from '@/lib/http'

/**
 * The last thing between a render-time throw and a blank page.
 *
 * React unmounts the whole tree when a render throws and nothing catches it, so
 * one bad `.map` over an unexpected shape took the entire admin app to white —
 * no message, no navigation, nothing to press. The SPA had no boundary
 * anywhere, so that was the behaviour for every route.
 *
 * A class component because this is the one thing hooks cannot do: React
 * exposes error catching only through the lifecycle methods.
 *
 * It deliberately does NOT try to recover in place. A tree that threw is a tree
 * whose state we do not understand, and re-rendering it usually throws again;
 * reloading is the honest offer. It also does not show the message — a stack
 * trace is not for the person reading it, and the detail is in the console for
 * whoever is debugging.
 *
 * The one exception is a WIRE CODE: when what escaped the render is an
 * HttpError, the code goes under the sentence in mono, exactly as QueryError
 * shows it (docs/ui-effects.md §6). No copy button, and never the message of
 * anything else — a JS TypeError's text is a stack trace by another name.
 */
export class ErrorBoundary extends Component<
  { children: ReactNode },
  { failed: boolean; code?: string }
> {
  state: { failed: boolean; code?: string } = { failed: false }

  static getDerivedStateFromError(error: unknown) {
    return { failed: true, code: error instanceof HttpError ? error.message : undefined }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // The console is where this belongs: it survives the reload the user is
    // about to do, and it carries the component stack, which is the part that
    // actually locates the bug.
    console.error('unhandled render error', error, info.componentStack)
  }

  render() {
    if (!this.state.failed) return this.props.children

    return (
      <div className="grid min-h-dvh place-items-center p-6">
        <div className="flex max-w-sm flex-col items-center gap-4 text-center">
          <IconTile size="lg" className="border-destructive/30 bg-destructive/10">
            <IconAlertTriangle className="size-6 text-destructive" />
          </IconTile>
          <div className="space-y-1">
            <p className="font-medium">This page stopped working</p>
            <p className="text-muted-foreground text-sm">
              Something broke while drawing the screen. Reloading usually fixes it.
            </p>
            {this.state.code ? (
              <p className="font-mono text-muted-foreground/70 text-xs">{this.state.code}</p>
            ) : null}
          </div>
          <Button type="button" onClick={() => window.location.reload()}>
            Reload
          </Button>
        </div>
      </div>
    )
  }
}
